import { EditorSelection, type StateCommand } from '@codemirror/state';
import { indentLess, indentMore } from '@codemirror/commands';
import type { EditorView, KeyBinding } from '@codemirror/view';
import type { UndoManager } from 'yjs';
import type { EditorCommands } from './index.jsx';

/** Editing commands + keymap for the note editor. */

const LIST_LINE = /^\s*([-*+]|\d+[.)])\s/;
const TASK_LINE = /^(\s*(?:[-*+]|\d+[.)])\s+)\[([ xX])\]/;

/** Toggle wrapping the selection in `marker` (bold/italic/etc.). */
export const toggleWrap =
  (marker: string): StateCommand =>
  ({ state, dispatch }) => {
    const len = marker.length;
    const tr = state.changeByRange((range) => {
      const { from, to } = range;
      const before = state.sliceDoc(Math.max(0, from - len), from);
      const after = state.sliceDoc(to, Math.min(state.doc.length, to + len));
      if (before === marker && after === marker) {
        return {
          changes: [
            { from: from - len, to: from },
            { from: to, to: to + len },
          ],
          range: EditorSelection.range(from - len, to - len),
        };
      }
      const inner = state.sliceDoc(from, to);
      if (inner.length >= 2 * len && inner.startsWith(marker) && inner.endsWith(marker)) {
        return {
          changes: [
            { from, to: from + len },
            { from: to - len, to },
          ],
          range: EditorSelection.range(from, to - 2 * len),
        };
      }
      return {
        changes: [
          { from, insert: marker },
          { from: to, insert: marker },
        ],
        range: EditorSelection.range(from + len, to + len),
      };
    });
    dispatch(state.update(tr, { scrollIntoView: true, userEvent: 'input' }));
    return true;
  };

/** Toggle (or create) the task checkbox on the cursor's line. */
export const toggleCheckboxCommand: StateCommand = ({ state, dispatch }) => {
  const line = state.doc.lineAt(state.selection.main.head);
  const m = TASK_LINE.exec(line.text);
  if (m) {
    const at = line.from + m[1].length + 1;
    dispatch(
      state.update({
        changes: { from: at, to: at + 1, insert: m[2] === ' ' ? 'x' : ' ' },
        userEvent: 'input',
      }),
    );
    return true;
  }
  const li = /^(\s*)([-*+]|\d+[.)])(\s+)/.exec(line.text);
  if (li) {
    dispatch(
      state.update({
        changes: { from: line.from + li[0].length, insert: '[ ] ' },
        userEvent: 'input',
      }),
    );
    return true;
  }
  const indent = /^\s*/.exec(line.text)![0].length;
  dispatch(
    state.update({
      changes: { from: line.from + indent, insert: '- [ ] ' },
      userEvent: 'input',
    }),
  );
  return true;
};

function selectionHasListLine(target: Parameters<StateCommand>[0]): boolean {
  const { state } = target;
  for (const r of state.selection.ranges) {
    let pos = r.from;
    for (;;) {
      const line = state.doc.lineAt(pos);
      if (LIST_LINE.test(line.text)) return true;
      if (line.to >= r.to) break;
      pos = line.to + 1;
    }
  }
  return false;
}

/** Tab indents list items; elsewhere inserts two spaces. */
const tabCommand: StateCommand = (target) => {
  if (selectionHasListLine(target)) return indentMore(target);
  const { state, dispatch } = target;
  dispatch(state.update(state.replaceSelection('  '), { scrollIntoView: true, userEvent: 'input' }));
  return true;
};

const shiftTabCommand: StateCommand = (target) => {
  if (selectionHasListLine(target)) return indentLess(target);
  return false;
};

export const editorKeymap: KeyBinding[] = [
  { key: 'Mod-b', run: toggleWrap('**') },
  { key: 'Mod-i', run: toggleWrap('*') },
  { key: 'Mod-Enter', run: toggleCheckboxCommand },
  { key: 'Tab', run: tabCommand, shift: shiftTabCommand },
];

/** Imperative commands for the mobile shortcut row. */
export function makeEditorCommands(view: EditorView, undoManager: UndoManager): EditorCommands {
  return {
    insertText(text, cursorOffset) {
      const { from, to } = view.state.selection.main;
      view.dispatch({
        changes: { from, to, insert: text },
        selection: { anchor: from + (cursorOffset ?? text.length) },
        scrollIntoView: true,
        userEvent: 'input',
      });
      view.focus();
    },
    toggleCheckbox() {
      toggleCheckboxCommand(view);
      view.focus();
    },
    wrapSelection(before, after) {
      const { from, to } = view.state.selection.main;
      view.dispatch({
        changes: [
          { from, insert: before },
          { from: to, insert: after },
        ],
        selection:
          from === to
            ? { anchor: from + before.length }
            : EditorSelection.range(from + before.length, to + before.length),
        scrollIntoView: true,
        userEvent: 'input',
      });
      view.focus();
    },
    undo() {
      undoManager.undo();
      view.focus();
    },
    redo() {
      undoManager.redo();
      view.focus();
    },
  };
}
